import { type PropsWithChildren } from "react";
import {
  type FieldValues,
  FormProvider as ReactHookFormProvider,
  type FormProviderProps as ReactHookFormProviderProps,
  useFormContext as useReactHookFormContext,
} from "react-hook-form";

import { FormTabsStateProvider } from "../form-tabs/form-tabs-state-provider";
import { IqfFormContext } from "./form-context";
import { isFieldRequired } from "./is-field-required";
import { type IqfFormContextType } from "./types";

function FormProvider_<TFieldValues extends FieldValues>({
  children,
  editing = true,
  disabled = false,
  formSchema,
}: PropsWithChildren<Partial<IqfFormContextType<TFieldValues>>>) {
  const reactHookFormContext = useReactHookFormContext<TFieldValues>();

  const isRequired = (path: string) => {
    if (!formSchema || !editing) {
      return false;
    }

    return isFieldRequired({
      formSchema,
      values: reactHookFormContext.getValues(),
      path,
    });
  };

  return (
    <IqfFormContext
      value={{
        editing,
        disabled,
        formSchema,
        isRequired,
        ...reactHookFormContext,
      }}
    >
      <FormTabsStateProvider>{children}</FormTabsStateProvider>
    </IqfFormContext>
  );
}

export function FormProvider<TFieldValues extends FieldValues>({
  children,
  editing,
  disabled,
  formSchema,
  ...rest
}: PropsWithChildren<
  ReactHookFormProviderProps<TFieldValues> &
    Partial<IqfFormContextType<TFieldValues>>
>) {
  return (
    <ReactHookFormProvider {...rest}>
      <FormProvider_
        editing={editing}
        disabled={disabled}
        formSchema={formSchema}
      >
        {children}
      </FormProvider_>
    </ReactHookFormProvider>
  );
}
